import React from 'react';
import { Box, Typography } from '@mui/material';

import CopyToClipboard from 'app/components/CopyToClipboard';

export type PageHeaderEntityId = {
  label: string;
  value: string;
};

export type PageHeaderEntityInfoProps = {
  /** Primary name shown above the ID row (e.g. dealer/make title). */
  title?: string | null;
  /** One or more labeled IDs rendered on a single line with copy icons. */
  ids: PageHeaderEntityId[];
};

/**
 * Shared page-header entity block: title + labeled IDs with copy.
 * Used for dealer headers and make/manufacturer headers.
 */
export const PageHeaderEntityInfo: React.FC<PageHeaderEntityInfoProps> = ({
  title,
  ids,
}) => {
  const visibleIds = (ids || []).filter(id => Boolean(id?.value));
  if (!title && visibleIds.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {title ? (
        <Typography variant="subtitle1" component="div">
          {title}
        </Typography>
      ) : null}
      {visibleIds.length > 0 ? (
        <Typography
          variant="subtitle2"
          component="div"
          sx={{
            fontWeight: 400,
            fontSize: '0.8rem',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          {visibleIds.map(id => (
            <Box
              key={`${id.label}-${id.value}`}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              {id.label}: {id.value}
              <CopyToClipboard text={id.value} size="small" />
            </Box>
          ))}
        </Typography>
      ) : null}
    </Box>
  );
};

export type DealerPageHeaderEntityInfoProps = {
  title?: string | null;
  dealerId?: string | null;
};

/** Convenience wrapper for dealer page headers. */
export const DealerPageHeaderEntityInfo: React.FC<
  DealerPageHeaderEntityInfoProps
> = ({ title, dealerId }) => (
  <PageHeaderEntityInfo
    title={title}
    ids={
      dealerId ? [{ label: 'Dealer ID', value: String(dealerId) }] : []
    }
  />
);

export type MakePageHeaderEntityInfoProps = {
  makeName?: string | null;
  manufacturerName?: string | null;
  makeId?: string | null;
  manufacturerId?: string | null;
};

/** Convenience wrapper for MyAutoIQ make/manufacturer page headers. */
export const MakePageHeaderEntityInfo: React.FC<
  MakePageHeaderEntityInfoProps
> = ({ makeName, manufacturerName, makeId, manufacturerId }) => {
  const title =
    makeName && manufacturerName
      ? `${makeName} (${manufacturerName})`
      : makeName || manufacturerName || null;

  return (
    <PageHeaderEntityInfo
      title={title}
      ids={[
        { label: 'Make ID', value: makeId || '' },
        { label: 'Manufacturer ID', value: manufacturerId || '' },
      ]}
    />
  );
};

export default PageHeaderEntityInfo;
